'use client';
import { createContext, type MouseEventHandler, type PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import { useModalState } from '@ev-tech/ui-core-components/Modal';

import { useExposeTracking } from '@/hooks/useExposeTracking';
import { TabKeys } from '@/components/property-expose/MediaTabs/MediaTabs.types';
import { LoadingSkeleton } from '@/components/property-expose/MediaModal/LoadingSkeleton';

const MediaModal = dynamic(() => import('@/components/property-expose/MediaModal/MediaModal').then((mod) => mod.MediaModal), {
  loading: () => <LoadingSkeleton />,
});

const ContactFormModal = dynamic(() =>
  import('@/components/property-expose/ContactForm/ContactFormModal').then((mod) => mod.ContactFormModal),
);

const WhatsAppModal = dynamic(() => import('@/components/property-expose/WhatsAppModal/WhatsAppModal').then((mod) => mod.WhatsAppModal));

export type ExposeContextType = {
  openMediaModal: () => void;
  setSelectedMediaImageIndex: (index: number) => void;
  setSelectedMediaTab: (tab: TabKeys) => void;
  setSelectedMediaFloorplanIndex: (index: number) => void;
  onContactModalTriggerClick: MouseEventHandler<HTMLElement>;
  openWhatsAppModal: (isMobile?: boolean) => void;
  isWhatsAppModalOpen?: boolean;
};

const initialState: ExposeContextType = {
  openMediaModal: () => {},
  setSelectedMediaImageIndex: () => {},
  setSelectedMediaTab: () => {},
  setSelectedMediaFloorplanIndex: () => {},
  onContactModalTriggerClick: () => {},
  openWhatsAppModal: () => {},
  isWhatsAppModalOpen: false,
};

export const ExposeContext = createContext<ExposeContextType>(initialState);

export const useExposeModal = (): ExposeContextType => {
  const context = useContext(ExposeContext);

  if (!context) {
    throw new Error('useExposeModal must be used within an ExposeModalProvider');
  }
  return context;
};

export const ExposeModalProvider = ({ children }: PropsWithChildren) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedTab, setSelectedTab] = useState<TabKeys>(TabKeys.Photos);
  const [selectedFloorplanIndex, setSelectedFloorplanIndex] = useState(0);
  const [isMobileWhatsAppModal, setIsMobileWhatsAppModal] = useState<boolean>(false);
  const { trackRequestExposeEvents } = useExposeTracking();

  const whatsAppModalState = useModalState();

  const contactModalState = useModalState();

  const mediaModalState = useModalState();

  const { open: openMediaModal } = mediaModalState;

  const handleRequestExposeButtonClick = useCallback<MouseEventHandler<HTMLElement>>(
    (e) => {
      const target = e.currentTarget;
      contactModalState.open();
      trackRequestExposeEvents({
        elementId: target.id || target.dataset.testid || '',
        navigationTarget: 'target',
      });
    },
    [contactModalState, trackRequestExposeEvents],
  );

  const openWhatsAppModal = useCallback(
    (isMobile: boolean = false) => {
      setIsMobileWhatsAppModal(isMobile);
      whatsAppModalState.open();
    },
    [whatsAppModalState],
  );

  const value = useMemo<ExposeContextType>(
    () => ({
      openMediaModal,
      setSelectedMediaImageIndex: setSelectedImageIndex,
      setSelectedMediaTab: setSelectedTab,
      setSelectedMediaFloorplanIndex: setSelectedFloorplanIndex,
      onContactModalTriggerClick: handleRequestExposeButtonClick,
      openWhatsAppModal,
      isWhatsAppModalOpen: whatsAppModalState.isOpen,
    }),
    [openMediaModal, handleRequestExposeButtonClick, openWhatsAppModal, whatsAppModalState.isOpen],
  );

  return (
    <ExposeContext.Provider value={value}>
      {children}
      {mediaModalState.isOpen && (
        <MediaModal
          modalState={mediaModalState}
          initialTab={selectedTab}
          onGalleryImageClick={setSelectedImageIndex}
          selectedImageIndex={selectedImageIndex}
          selectedFloorplanIndex={selectedFloorplanIndex}
          data-testid="expose-media-modal"
        />
      )}
      {contactModalState.isOpen && <ContactFormModal modalState={contactModalState} />}
      {whatsAppModalState.isOpen && <WhatsAppModal modalState={whatsAppModalState} isMobile={isMobileWhatsAppModal} />}
    </ExposeContext.Provider>
  );
};
